import { useEffect, useState } from "react";
import { Radio, MapPin, Trophy, RefreshCw, CalendarDays, Signal, Zap } from "lucide-react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";

const FORMAT_COLOR = {
  TEST: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  ODI: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  T20I: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  OTHER: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

function statusPill(m) {
  const s = (m.status || "").toLowerCase();
  if (m.matchEnded || /won by|drawn|no result/.test(s)) {
    return { cls: "bg-slate-800 text-slate-400 border-slate-700", label: "Result" };
  }
  if (m.matchStarted || /day|innings|stumps|tea|lunch/.test(s)) {
    return { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", label: "Live" };
  }
  return { cls: "bg-sky-500/15 text-sky-300 border-sky-500/30", label: "Upcoming" };
}

function formatDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function LiveFeedPage() {
  const [data, setData] = useState({ matches: [], cached: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/live/matches${force ? "?force=true" : ""}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load live matches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); }, []);

  const live = data.matches.filter((m) => statusPill(m).label === "Live");
  const upcoming = data.matches.filter((m) => statusPill(m).label === "Upcoming");
  const finished = data.matches.filter((m) => statusPill(m).label === "Result");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="section-label">Real-world feed</div>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-1 flex items-center gap-3">
            <Signal className="w-9 h-9 text-emerald-400" /> Live Feed
          </h1>
          <p className="text-slate-400 text-sm mt-2 max-w-lg">
            Real international, domestic and franchise cricket pulled from cricketdata.org. Read-only for now — prediction resolution on live matches ships next.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.cached && (
            <span className="text-[11px] font-mono-data text-slate-500">
              Cached{data.stale ? " (stale)" : ""} · {data.cached_age_sec}s ago
            </span>
          )}
          <button onClick={() => load(true)} disabled={loading} className="btn-ghost text-xs" data-testid="live-refresh">
            <RefreshCw className={`w-3.5 h-3.5 inline mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {loading && !data.matches.length && <div className="text-slate-400 text-sm">Loading live feed…</div>}
      {error && <div className="tile p-6 border-rose-500/40 text-rose-300 text-sm">{error}</div>}

      {!error && (
        <>
          <Section title="On now" items={live} accent="emerald" testid="live-section-live" />
          <Section title="Upcoming" items={upcoming} accent="sky" testid="live-section-upcoming" />
          <Section title="Latest results" items={finished} accent="slate" testid="live-section-results" />
        </>
      )}

      <p className="text-[11px] text-slate-500 font-mono-data mt-6">
        Source: cricketdata.org. Free tier 100 req/day — list refreshes hourly, match detail every 20 min.
      </p>
    </div>
  );
}

function Section({ title, items, accent, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-10" data-testid={testid}>
      <div className={`section-label mb-3 text-${accent}-300`}>{title} · {items.length}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger">
        {items.map((m) => <MatchCard key={m.id} m={m} />)}
      </div>
    </div>
  );
}

function MatchCard({ m }) {
  const pill = statusPill(m);
  const fmt = FORMAT_COLOR[m.matchType] || FORMAT_COLOR.OTHER;
  const [a, b] = m.teams || ["TBA", "TBA"];
  const scoreA = m.score?.find?.((s) => (s.inning || "").toLowerCase().startsWith((a || "").toLowerCase().slice(0, 6))) || m.score?.[0];
  const scoreB = m.score?.find?.((s) => (s.inning || "").toLowerCase().startsWith((b || "").toLowerCase().slice(0, 6))) || m.score?.[1];
  const canPredict = pill.label === "Live";

  return (
    <div className="tile p-5 grain relative overflow-hidden" data-testid={`live-match-card-${m.id}`}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className={`pill border ${fmt}`}>{m.matchType}</span>
        <span className={`pill border ${pill.cls}`}>
          {pill.label === "Live" && <span className="live-dot" />}
          {pill.label}
        </span>
        <span className="ml-auto text-xs text-slate-400 flex items-center gap-1"><CalendarDays className="w-3 h-3" />{formatDate(m.date)}</span>
      </div>

      <div className="font-display font-black text-lg sm:text-xl uppercase tracking-tight leading-tight">{m.name}</div>
      {m.venue && <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{m.venue}</div>}

      <div className="mt-3 flex gap-4 items-baseline flex-wrap">
        <TeamScore team={a} s={scoreA} />
        <TeamScore team={b} s={scoreB} />
      </div>

      {m.status && (
        <div className={`mt-3 text-xs ${pill.label === "Live" ? "text-emerald-300" : "text-slate-400"}`}>{m.status}</div>
      )}

      {canPredict && (
        <Link
          to={`/live/match/${m.id}`}
          className="mt-4 inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 font-semibold text-xs uppercase tracking-wider group"
          data-testid={`live-predict-link-${m.id}`}
        >
          <Zap className="w-3.5 h-3.5" /> Predict over-by-over
          <span className="group-hover:translate-x-1 transition-transform">→</span>
        </Link>
      )}
    </div>
  );
}

function TeamScore({ team, s }) {
  return (
    <div className="min-w-[120px]">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono-data">{team}</div>
      {s ? (
        <div className="metric-value text-emerald-400 text-lg">
          {s.r}/{s.w}
          <span className="text-slate-500 text-xs font-mono-data ml-1">({s.o})</span>
        </div>
      ) : (
        <div className="text-slate-500 font-mono-data text-sm">—</div>
      )}
    </div>
  );
}
